import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { signInWithEmailAndPassword } from 'firebase/auth'; 
import { getDocs, collection} from 'firebase/firestore'; 
import { auth, db } from '../firebaseConfig'; 
import logo from '../img/logo.png';
import { useTranslation } from 'react-i18next';

// Styled Components
const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
});

const PaperContainer = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  borderRadius: '5px',
  width: '90%',
  maxWidth: '600px',
  boxSizing: 'border-box',
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '300px',
  height: 'auto',
}));

const AvatarIcon = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
  boxSizing: 'border-box',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

export default function AdminLogin() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";
  const fieldDirection = isRtl ? "rtl" : "ltr";
  const authFieldSx = {
    direction: fieldDirection,
    "& .MuiInputLabel-root": {
      left: isRtl ? "auto" : 0,
      right: isRtl ? 0 : "auto",
      transformOrigin: isRtl ? "top right" : "top left",
      textAlign: isRtl ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      textAlign: isRtl ? "right" : "left",
    },
    "& input": {
      textAlign: isRtl ? "right" : "left",
    },
  };

  // ===== Language switcher (top-right) =====
  const LANGS = [
    { code: "ar", label: "AR" },
    { code: "fr", label: "FR" },
    { code: "en", label: "EN" },
  ];

  const handleChangeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem("i18nextLng", langCode);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { email, password } = formData;

    try {
      // Check if the email exists in the Admin collection
      const adminSnapshot = await getDocs(collection(db, 'admin'));
      const adminDoc = adminSnapshot.docs.find(
        (doc) => String(doc.data().email || "").trim().toLowerCase() === email.trim().toLowerCase()
      );

      if (!adminDoc) {
        throw new Error(t('البريد الإلكتروني غير موجود في مجموعة الإدارة !! يرجى التواصل مع الدعم'));
      }

      // Sign in the admin using Firebase Auth
      await signInWithEmailAndPassword(auth, email, password);
      const role = String(adminDoc.data().role || "").trim().toLowerCase();
      navigate(role === "marketing" ? "/06071966/blogs" : "/06071966/AdminAllStudents");
      
    } catch (err) {
      console.error('Error logging in:', err);
      setError(t('البريد الإلكتروني أو كلمة المرور غير صحيحة. يرجى المحاولة مرة أخرى'));
    }
  };


  return (
    <Box
      sx={{
        position: "relative",
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
      }}
    >
      {/* Top-right language switcher */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          display: "flex",
          gap: 1,
          zIndex: 10,
        }}
      >
        {LANGS.map((l) => {
          const active = i18n.language?.startsWith(l.code);
          return (
            <Button
              key={l.code}
              size="small"
              variant={active ? "contained" : "outlined"}
              onClick={() => handleChangeLanguage(l.code)}
              sx={{
                minWidth: 46,
                borderRadius: 999,
                fontWeight: 800,
                textTransform: "none",
                ...(active
                  ? { bgcolor: "#12344d", "&:hover": { bgcolor: "#0f2b3e" } }
                  : { borderColor: "rgba(0,0,0,0.15)", color: "#111827" }),
              }}
            >
              {l.label}
            </Button>
          );
        })}
      </Box>
      <CssBaseline />
      <CenteredContainer component="main" maxWidth="sm">
        <LogoImage src={logo} alt={t('Logo')} />
        <PaperContainer>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 500,
                color: "#111827",
                mb: 0.5,
              }}
            >
              {t('Login Page')}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                fontWeight: 400,
              }}
            >
              {t('Administration Staff Only')}
            </Typography>
          </Box>
          
          <Form 
              noValidate 
              onSubmit={handleSubmit}
              dir={fieldDirection}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); 
                  handleSubmit(e); 
                }
              }}
            >
            <TextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('Email Address')}
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              inputProps={{ dir: fieldDirection }}
              sx={authFieldSx}
            />
            <TextField
              variant="standard"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('Password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              inputProps={{ dir: fieldDirection }}
              sx={authFieldSx}
            />

              {error && <Typography color="error" sx={{ marginTop: 2, fontSize: '15px' }}>{error}</Typography>}


            <SubmitButton
              type="submit"
              fullWidth
              variant="contained"
              
              sx={{borderRadius: '20px', backgroundColor:"#12344d"}}
            >
              {t('Login')}
            </SubmitButton>
            {/* <Container>
              <Link href="/06071966/register" variant="body2">
                {t("Register my account first !!")}
              </Link>
            </Container> */}

            {/* <Container>
              <Container>
                <Link
                  variant="body2"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  href="https://houari-boumedien-cem.web.app/06071966/home" 
                >
                  {t("التوجه إلى الطور المتوسط")}
                </Link>
              </Container>

            </Container> */}
          </Form>
        </PaperContainer>
      </CenteredContainer>
    </Box>
  );
}
